import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiTrendingUp, FiShield, FiDollarSign, FiCheckCircle, FiArrowRight, FiX,
  FiBarChart3, FiTarget, FiUsers, FiHome, FiClock, FiHeart, FiStar,
  FiBriefcase, FiPieChart, FiArrowUp, FiArrowDown, FiLock, FiUnlock,
  FiPercent, FiCalendar, FiRefreshCw, FiZap, FiAlertTriangle, FiInfo
} = FiIcons;

const IULPage = () => {
  const benefits = [
    'Death benefit protection for life',
    'Cash value growth linked to market index performance',
    'Guaranteed minimum interest rate protection',
    'Flexible premium payments and death benefits',
    'Tax-advantaged policy loans and withdrawals',
    'No direct stock market exposure or risk'
  ];

  const howIULWorks = [
    {
      step: 1,
      title: 'Premium Payment',
      description: 'Your premium splits into two parts: insurance costs and cash value building',
      icon: FiDollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      step: 2,
      title: 'Index Tracking',
      description: 'Cash value earns interest based on market index performance (like S&P 500)',
      icon: FiBarChart3,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      step: 3,
      title: 'Protected Growth',
      description: 'Caps limit maximum gains, floors protect against losses',
      icon: FiShield,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      step: 4,
      title: 'Flexible Access',
      description: 'Borrow against cash value or adjust premiums as needed',
      icon: FiRefreshCw,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  const whoShouldConsider = [
    {
      icon: FiTrendingUp,
      title: 'Growth-Minded Savers',
      description: 'Want market upside potential without direct stock risk',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      scenarios: [
        'Seek index-linked returns',
        'Want downside protection',
        'Long-term wealth building',
        'Tax-advantaged growth'
      ]
    },
    {
      icon: FiBriefcase,
      title: 'High-Income Earners',
      description: 'Professionals with variable income needing flexible premiums',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      scenarios: [
        'Variable income patterns',
        'Max out other retirement accounts',
        'Need premium flexibility',
        'Estate planning goals'
      ]
    },
    {
      icon: FiHome,
      title: 'Estate Planners',
      description: 'Want permanent coverage with wealth transfer benefits',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      scenarios: [
        'Legacy planning',
        'Tax-free death benefits',
        'Wealth preservation',
        'Multi-generational planning'
      ]
    },
    {
      icon: FiTarget,
      title: 'Retirement Planners',
      description: 'Supplement retirement with tax-advantaged cash access',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      scenarios: [
        'Retirement income supplement',
        'Tax diversification',
        'Flexible withdrawal options',
        'Long-term planning horizon'
      ]
    }
  ];

  const whoShouldNotConsider = [
    {
      icon: FiClock,
      title: 'Short-Term Needs',
      description: 'Need coverage for less than 15-20 years',
      reason: 'Time Horizon Too Short',
      alternative: 'Term life insurance offers better value for temporary needs'
    },
    {
      icon: FiDollarSign,
      title: 'Budget-Constrained',
      description: 'Cannot afford higher premiums consistently',
      reason: 'Premium Affordability',
      alternative: 'Term life provides maximum coverage at lowest cost'
    },
    {
      icon: FiZap,
      title: 'Simplicity Seekers',
      description: 'Want straightforward, simple life insurance',
      reason: 'Complexity Concerns',
      alternative: 'Whole life offers simpler guaranteed benefits'
    },
    {
      icon: FiPercent,
      title: 'Maximum Return Seekers',
      description: 'Want highest possible investment returns',
      reason: 'Return Limitations',
      alternative: 'Direct investing may offer higher potential returns'
    }
  ];

  const comparisonData = [
    {
      feature: 'Coverage Duration',
      iul: 'Lifetime',
      wholeLife: 'Lifetime',
      termLife: '10-30 years',
      universal: 'Lifetime'
    },
    {
      feature: 'Premium Flexibility',
      iul: 'Flexible',
      wholeLife: 'Fixed',
      termLife: 'Fixed',
      universal: 'Flexible'
    },
    {
      feature: 'Cash Value Growth',
      iul: 'Index-linked',
      wholeLife: 'Guaranteed',
      termLife: 'None',
      universal: 'Fixed rate'
    },
    {
      feature: 'Market Protection',
      iul: 'Yes (floors)',
      wholeLife: 'Yes',
      termLife: 'N/A',
      universal: 'No'
    },
    {
      feature: 'Complexity',
      iul: 'Moderate-High',
      wholeLife: 'Low',
      termLife: 'Low',
      universal: 'Moderate'
    }
  ];

  const returnsExample = {
    indexGain: 12,
    scenarios: [
      {
        type: 'Cap Only',
        cap: 10,
        participationRate: 100,
        creditedRate: 10,
        explanation: 'Capped at maximum 10%'
      },
      {
        type: 'Participation Rate Only',
        cap: null,
        participationRate: 80,
        creditedRate: 9.6,
        explanation: '80% of 12% index gain'
      },
      {
        type: 'Both Applied',
        cap: 10,
        participationRate: 80,
        creditedRate: 9.6,
        explanation: 'Lower of cap (10%) or participation (9.6%)'
      }
    ]
  };

  const prosAndCons = {
    pros: [
      'Market upside potential without direct risk',
      'Guaranteed minimum interest rate protection',
      'Flexible premium and death benefit options',
      'Tax-advantaged cash value growth',
      'Policy loan access for liquidity needs',
      'Permanent life insurance coverage'
    ],
    cons: [
      'Complex product with multiple moving parts',
      'Fees can reduce overall returns significantly',
      'Caps limit maximum growth potential',
      'Poor management can lead to policy lapse',
      'Higher costs than term life insurance',
      'Returns dependent on insurer crediting methods'
    ]
  };

  const faqs = [
    {
      question: 'What is the difference between IUL and Universal Life?',
      answer: 'IUL ties growth to a stock index, while standard universal life uses fixed or variable rates. IUL offers a balance between fixed growth and potential upside, while still protecting against market losses.'
    },
    {
      question: 'Can you lose money in an IUL?',
      answer: 'IUL policies include a guaranteed minimum interest rate. While fees may reduce growth, the floor rate helps prevent loss of principal due to poor market performance.'
    },
    {
      question: 'Are IUL loans tax-free?',
      answer: 'Loans from your IUL policy usually avoid taxes if the policy stays active and doesn\'t become a Modified Endowment Contract. Always consult a tax professional.'
    },
    {
      question: 'How much does IUL cost?',
      answer: 'Costs depend on age, health, coverage amount, and insurer. IUL typically costs more than term life but offers added value through flexible structure and growth features.'
    }
  ];

  const testimonials = [
    {
      name: 'Michael Chen',
      age: 42,
      location: 'San Francisco, CA',
      quote: 'The IUL policy gives me the growth potential I want with the protection I need. The flexibility to adjust premiums based on my business income is invaluable.',
      coverage: 'IUL Policy with $750,000 coverage'
    },
    {
      name: 'Sarah Martinez',
      age: 38,
      location: 'Los Angeles, CA',
      quote: 'After maxing out my 401k and IRA, IUL provides another tax-advantaged way to build wealth while protecting my family. The index linking is brilliant.',
      coverage: 'IUL Policy with $500,000 coverage'
    },
    {
      name: 'David Thompson',
      age: 45,
      location: 'San Diego, CA',
      quote: 'As a business owner with variable income, I love that I can adjust my premiums. The cash value growth has exceeded my expectations over 5 years.',
      coverage: 'IUL Policy with $1,000,000 coverage'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 to-purple-700 pt-24 pb-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiTrendingUp} className="h-8 w-8 mr-3" />
                <span className="font-semibold">Indexed Universal Life Insurance</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Life Insurance That Grows With the Market
              </h1>
              <p className="text-xl text-indigo-100 mb-8">
                Combine permanent life insurance protection with cash value growth linked to market index performance. Get upside potential with downside protection.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://quote.burbankinsuranceservices.com/Dt2tCn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
                >
                  Get My IUL Quote
                  <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
                </a>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg p-8 text-secondary-900"
            >
              <h3 className="text-2xl font-bold mb-6">IUL Key Features</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                  <span className="font-medium">Growth Potential</span>
                  <span className="text-indigo-600 font-bold">Index-Linked</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                  <span className="font-medium">Downside Protection</span>
                  <span className="text-indigo-600 font-bold">Guaranteed Floor</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                  <span className="font-medium">Premium Flexibility</span>
                  <span className="text-indigo-600 font-bold">Adjustable</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                  <span className="font-medium">Coverage Duration</span>
                  <span className="text-indigo-600 font-bold">Lifetime</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How IUL Works - Infographic Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              How Does Indexed Universal Life Insurance Work?
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              IUL combines life insurance protection with cash value growth linked to market index performance, 
              offering upside potential with built-in protection against losses.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howIULWorks.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className={`${step.bgColor} rounded-xl p-6 text-center h-full`}>
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-white rounded-full p-4 shadow-lg">
                      <SafeIcon icon={step.icon} className={`h-8 w-8 ${step.color}`} />
                    </div>
                  </div>
                  <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-4">
                    <span className="text-lg font-bold text-secondary-900">{step.step}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-secondary-900 mb-3">{step.title}</h3>
                  <p className="text-secondary-700">{step.description}</p>
                </div>
                {index < howIULWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <SafeIcon icon={FiArrowRight} className="h-6 w-6 text-secondary-400" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Returns Calculation Infographic */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              How Are IUL Returns Calculated?
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              IUL returns are based on market index performance but are limited by caps and participation rates. 
              Here's how it works with a 12% index gain example:
            </p>
          </motion.div>

          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center bg-green-100 rounded-full px-6 py-3 mb-4">
                <SafeIcon icon={FiBarChart3} className="h-6 w-6 text-green-600 mr-2" />
                <span className="font-bold text-green-800">S&P 500 Index Gain: {returnsExample.indexGain}%</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {returnsExample.scenarios.map((scenario, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-secondary-50 rounded-lg p-6 text-center"
                >
                  <h3 className="text-xl font-bold text-secondary-900 mb-4">{scenario.type}</h3>
                  
                  <div className="space-y-3 mb-4">
                    {scenario.cap && (
                      <div className="flex justify-between">
                        <span className="text-secondary-600">Cap Rate:</span>
                        <span className="font-bold text-red-600">{scenario.cap}%</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Participation Rate:</span>
                      <span className="font-bold text-blue-600">{scenario.participationRate}%</span>
                    </div>
                  </div>

                  <div className="border-t-2 border-secondary-200 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-secondary-900">Credited Rate:</span>
                      <span className="text-2xl font-bold text-green-600">{scenario.creditedRate}%</span>
                    </div>
                    <p className="text-sm text-secondary-600 mt-2">{scenario.explanation}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who Should Consider IUL - Infographic */}
      <section className="py-16 bg-gradient-to-br from-secondary-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Is Indexed Universal Life Insurance Right for You?
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              IUL works best for specific financial situations and goals. Here's who should consider it and who has better alternatives.
            </p>
          </motion.div>

          {/* Who SHOULD Consider */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-green-700 mb-2">✅ Perfect Candidates for IUL</h3>
              <p className="text-secondary-600">These situations make IUL an excellent choice</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${candidate.bgColor} ${candidate.borderColor} border-2 rounded-xl p-6 hover:shadow-lg transition-shadow`}
                >
                  <div className="text-center mb-4">
                    <div className={`${candidate.bgColor} rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border ${candidate.borderColor}`}>
                      <SafeIcon icon={candidate.icon} className={`h-8 w-8 ${candidate.color}`} />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-4">{candidate.description}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-secondary-800 mb-2 text-sm">Perfect for:</h5>
                    <ul className="space-y-1">
                      {candidate.scenarios.map((scenario, idx) => (
                        <li key={idx} className="text-xs text-secondary-700 flex items-center">
                          <SafeIcon icon={FiCheckCircle} className={`h-3 w-3 ${candidate.color} mr-2`} />
                          {scenario}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Who Should NOT Consider */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-red-700 mb-2">❌ Better Alternatives Available</h3>
              <p className="text-secondary-600">These situations typically have better, more suitable options</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldNotConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-red-50 border-2 border-red-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="text-center mb-4">
                    <div className="bg-red-100 rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border border-red-200">
                      <SafeIcon icon={candidate.icon} className="h-8 w-8 text-red-600" />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-3">{candidate.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-red-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-red-800">Why Not:</p>
                      <p className="text-xs text-red-700">{candidate.reason}</p>
                    </div>
                    <div className="bg-green-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-green-800">Better Option:</p>
                      <p className="text-xs text-green-700">{candidate.alternative}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IUL vs Other Policies Comparison */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              IUL vs. Other Life Insurance Policies
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Compare IUL with other life insurance options to see which best fits your needs.
            </p>
          </motion.div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-indigo-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center font-semibold bg-indigo-700">
                      <SafeIcon icon={FiTrendingUp} className="h-5 w-5 inline mr-2" />
                      IUL
                    </th>
                    <th className="px-6 py-4 text-center font-semibold">Whole Life</th>
                    <th className="px-6 py-4 text-center font-semibold">Term Life</th>
                    <th className="px-6 py-4 text-center font-semibold">Universal Life</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200">
                  {comparisonData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-secondary-50' : 'bg-white'}>
                      <td className="px-6 py-4 font-medium text-secondary-900">{row.feature}</td>
                      <td className="px-6 py-4 text-center font-semibold text-indigo-600 bg-indigo-50">{row.iul}</td>
                      <td className="px-6 py-4 text-center text-secondary-700">{row.wholeLife}</td>
                      <td className="px-6 py-4 text-center text-secondary-700">{row.termLife}</td>
                      <td className="px-6 py-4 text-center text-secondary-700">{row.universal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Pros and Cons */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Pros & Cons of Indexed Universal Life Insurance
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Like any financial product, IUL has advantages and disadvantages. Here's what you need to know.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pros */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <div className="flex items-center mb-6">
                <div className="bg-green-100 rounded-full p-3 mr-4">
                  <SafeIcon icon={FiCheckCircle} className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-secondary-900">Advantages</h3>
              </div>
              <ul className="space-y-4">
                {prosAndCons.pros.map((pro, index) => (
                  <li key={index} className="flex items-start">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-secondary-700">{pro}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Cons */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <div className="flex items-center mb-6">
                <div className="bg-red-100 rounded-full p-3 mr-4">
                  <SafeIcon icon={FiAlertTriangle} className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-secondary-900">Considerations</h3>
              </div>
              <ul className="space-y-4">
                {prosAndCons.cons.map((con, index) => (
                  <li key={index} className="flex items-start">
                    <SafeIcon icon={FiX} className="h-5 w-5 text-red-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-secondary-700">{con}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-secondary-600">
              Common questions about Indexed Universal Life Insurance
            </p>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-secondary-50 rounded-lg p-6"
              >
                <div className="flex items-start">
                  <SafeIcon icon={FiInfo} className="h-6 w-6 text-indigo-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-2">{faq.question}</h3>
                    <p className="text-secondary-700">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              What Our IUL Customers Say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-lg"
              >
                <blockquote className="text-secondary-700 mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t border-secondary-200 pt-4">
                  <p className="font-semibold text-secondary-900">{testimonial.name}</p>
                  <p className="text-sm text-secondary-600">{testimonial.location}</p>
                  <p className="text-sm text-indigo-600 font-medium mt-1">{testimonial.coverage}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-4">
              Ready to Explore Indexed Universal Life Insurance?
            </h2>
            <p className="text-xl text-indigo-100 mb-8">
              Get a free quote online and find life insurance coverage that fits your budget and goals—all without the hassle of a medical exam.
            </p>
            <a
              href="https://quote.burbankinsuranceservices.com/Dt2tCn"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors text-lg"
            >
              Get My IUL Quote
              <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
            </a>
            <p className="mt-4 text-sm text-indigo-200">
              Just a few health questions • No medical exam required • Powered by Ethos
            </p>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-secondary-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-secondary-600 mb-2">
              <strong>Disclaimer:</strong> The information and content provided is for informational purposes only, and it is not to be considered legal, tax, investment, or financial advice, recommendation, or endorsement.
            </p>
            <p className="text-xs text-secondary-500">
              You should consult with an attorney or other professional to determine what may be best for your individual needs. 
              Issuance of a policy may depend on your answers to health questions provided in the application.
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default IULPage;